// Validates efmodel documents: the JSON Schema shape first, then semantic checks (keys, foreign keys,
// inheritance, owned types, renames). Issues carry a path and a hint so agents can fix their output.
import type { z } from 'zod';
import { DocumentSchema, type EfModelDocument, type EntitySpec, type IndexSpec, type ModelSpec, type OwnedSpec, type PropertySpec } from './schema';

export interface ValidationIssue {
  severity: 'error' | 'warning';
  code: string;
  /** Dotted path into the document, e.g. `model.entities.Blog.key[0]`. */
  path: string;
  message: string;
  hint?: string;
}

export interface ValidationResult {
  ok: boolean;
  document: EfModelDocument | null;
  issues: ValidationIssue[];
}

function pathString(path: ReadonlyArray<PropertyKey>): string {
  let out = '';
  for (const p of path) {
    if (typeof p === 'number') out += `[${p}]`;
    else out += out ? `.${String(p)}` : String(p);
  }
  return out;
}

/** Parses JSON text and validates it. */
export function parseDocument(text: string): ValidationResult {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch (e) {
    return {
      ok: false,
      document: null,
      issues: [{ severity: 'error', code: 'E_JSON', path: '', message: `Not valid JSON: ${(e as Error).message}` }],
    };
  }
  return validateDocument(data);
}

export function validateDocument(input: unknown): ValidationResult {
  const parsed = DocumentSchema.safeParse(input);
  if (!parsed.success) {
    return { ok: false, document: null, issues: parsed.error.issues.map(schemaIssue) };
  }
  const issues = semanticIssues(parsed.data);
  return { ok: !issues.some((i) => i.severity === 'error'), document: parsed.data, issues };
}

function schemaIssue(issue: z.core.$ZodIssue): ValidationIssue {
  const path = pathString(issue.path);
  if (issue.code === 'unrecognized_keys') {
    return {
      severity: 'error',
      code: 'E_SCHEMA_UNKNOWN_KEY',
      path,
      message: `Unknown key${issue.keys.length > 1 ? 's' : ''}: ${issue.keys.join(', ')}`,
      hint: 'Check the spelling against the efmodel JSON Schema. Put tool-specific data under "extensions".',
    };
  }
  return { severity: 'error', code: 'E_SCHEMA', path, message: issue.message };
}

interface Node {
  kind: 'entity' | 'owned';
  path: string;
  spec: EntitySpec | OwnedSpec;
}

/** Resolves an entity key or an owned path such as `Order.Shipping.Dimensions`. */
export function resolveNode(model: ModelSpec, name: string): Node | null {
  if (model.entities[name]) return { kind: 'entity', path: `model.entities.${name}`, spec: model.entities[name]! };
  const segments = name.split('.');
  for (let i = segments.length - 1; i >= 1; i--) {
    const prefix = segments.slice(0, i).join('.');
    const entity = model.entities[prefix];
    if (!entity) continue;
    let spec: EntitySpec | OwnedSpec = entity;
    let path = `model.entities.${prefix}`;
    let ok = true;
    for (const nav of segments.slice(i)) {
      const next: OwnedSpec | undefined = spec.owned?.[nav];
      if (!next) {
        ok = false;
        break;
      }
      spec = next;
      path += `.owned.${nav}`;
    }
    if (ok) return { kind: 'owned', path, spec };
  }
  return null;
}

/** Properties visible on an entity, including inherited ones. */
function allProperties(model: ModelSpec, node: Node): Record<string, PropertySpec> {
  if (node.kind === 'owned') return node.spec.properties;
  const out: Record<string, PropertySpec> = {};
  const chain: EntitySpec[] = [];
  let current: EntitySpec | undefined = node.spec as EntitySpec;
  const seen = new Set<EntitySpec>();
  while (current && !seen.has(current)) {
    seen.add(current);
    chain.unshift(current);
    current = current.base ? model.entities[current.base] : undefined;
  }
  for (const e of chain) Object.assign(out, e.properties);
  return out;
}

function keyOf(model: ModelSpec, node: Node): string[] | null {
  if (node.kind === 'owned') {
    const o = node.spec as OwnedSpec;
    return o.key ?? (o.ownerKey.length ? o.ownerKey : null);
  }
  let current: EntitySpec | undefined = node.spec as EntitySpec;
  const seen = new Set<EntitySpec>();
  while (current && !seen.has(current)) {
    seen.add(current);
    if (current.key) return current.key;
    current = current.base ? model.entities[current.base] : undefined;
  }
  return null;
}

function alternateKeysOf(model: ModelSpec, node: Node): string[][] {
  if (node.kind === 'owned') return [];
  const out: string[][] = [];
  let current: EntitySpec | undefined = node.spec as EntitySpec;
  const seen = new Set<EntitySpec>();
  while (current && !seen.has(current)) {
    seen.add(current);
    out.push(...(current.alternateKeys ?? []));
    current = current.base ? model.entities[current.base] : undefined;
  }
  return out;
}

const STRING_LIKE = new Set(['string', 'byte[]', 'char[]']);

function semanticIssues(doc: EfModelDocument): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const model = doc.model;
  const error = (code: string, path: string, message: string, hint?: string) => issues.push({ severity: 'error', code, path, message, hint });
  const warn = (code: string, path: string, message: string, hint?: string) => issues.push({ severity: 'warning', code, path, message, hint });

  const checkProperties = (path: string, props: Record<string, PropertySpec>) => {
    for (const [name, p] of Object.entries(props)) {
      const pp = `${path}.properties.${name}`;
      if (p.type.endsWith('?')) {
        error('E_NULLABLE_TYPE', `${pp}.type`, `Write the type without "?".`, `Use "type": "${p.type.slice(0, -1)}" with "required": false.`);
      }
      if (p.scale !== undefined && p.precision !== undefined && p.scale > p.precision) {
        error('E_SCALE', pp, `Scale ${p.scale} is larger than precision ${p.precision}.`);
      }
      if (p.maxLength !== undefined && !STRING_LIKE.has(p.type.replace(/\?$/, '')) && !p.collection) {
        warn('W_MAXLENGTH_TYPE', pp, `maxLength has no effect on type ${p.type}.`);
      }
    }
  };

  const checkIndexes = (path: string, indexes: IndexSpec[] | undefined, visible: Record<string, PropertySpec>) => {
    (indexes ?? []).forEach((ix, i) => {
      for (const p of ix.properties) {
        if (!visible[p]) error('E_INDEX_PROPERTY', `${path}.indexes[${i}]`, `Index property "${p}" does not exist.`);
      }
      if (ix.descending && ix.descending.length !== ix.properties.length) {
        error('E_INDEX_DESCENDING', `${path}.indexes[${i}].descending`, 'descending must have one entry per index property.');
      }
    });
  };

  const checkOwned = (path: string, owner: EntitySpec | OwnedSpec) => {
    for (const [nav, o] of Object.entries(owner.owned ?? {})) {
      const op = `${path}.owned.${nav}`;
      if (owner.properties[nav]) error('E_NAME_CLASH', op, `"${nav}" is both a property and an owned navigation.`);
      for (const k of o.ownerKey) if (!o.properties[k]) error('E_OWNER_KEY', `${op}.ownerKey`, `Owner key property "${k}" is not declared in the owned type.`);
      for (const k of o.key ?? []) if (!o.properties[k]) error('E_KEY_PROPERTY', `${op}.key`, `Key property "${k}" does not exist.`);
      checkProperties(op, o.properties);
      checkIndexes(op, o.indexes, o.properties);
      checkOwned(op, o);
    }
    for (const name of Object.keys(owner.complex ?? {})) {
      if (owner.properties[name]) error('E_NAME_CLASH', `${path}.complex.${name}`, `"${name}" is both a property and a complex property.`);
    }
  };

  // Entities
  for (const [name, e] of Object.entries(model.entities)) {
    const path = `model.entities.${name}`;
    const node: Node = { kind: 'entity', path, spec: e };
    if (e.base) {
      if (!model.entities[e.base]) {
        error('E_BASE_MISSING', `${path}.base`, `Base entity "${e.base}" does not exist.`);
      } else {
        const seen = new Set([name]);
        let b: string | undefined = e.base;
        while (b) {
          if (seen.has(b)) {
            error('E_BASE_CYCLE', `${path}.base`, `Inheritance cycle through "${b}".`);
            break;
          }
          seen.add(b);
          b = model.entities[b]?.base;
        }
      }
      if (e.key) error('E_DERIVED_KEY', `${path}.key`, 'A derived entity inherits its key and cannot declare one.', `Remove "key" or remove "base".`);
      if (e.mapping?.strategy) error('E_STRATEGY_ON_DERIVED', `${path}.mapping.strategy`, 'The inheritance strategy belongs on the root entity.');
    } else if (!e.key && !e.keyless) {
      error('E_NO_KEY', path, 'Entity has no primary key.', 'Add "key": ["Id"] (and an Id property), or set "keyless": true for views and query results.');
    }
    const visible = allProperties(model, node);
    for (const k of e.key ?? []) {
      if (!visible[k]) error('E_KEY_PROPERTY', `${path}.key`, `Key property "${k}" does not exist.`);
      else if (!visible[k]!.required) warn('W_NULLABLE_KEY', `${path}.properties.${k}`, `Key property "${k}" should be required.`);
    }
    (e.alternateKeys ?? []).forEach((ak, i) => {
      for (const k of ak) if (!visible[k]) error('E_KEY_PROPERTY', `${path}.alternateKeys[${i}]`, `Key property "${k}" does not exist.`);
    });
    if (e.mapping?.discriminator && !visible[e.mapping.discriminator]) {
      error('E_DISCRIMINATOR_PROPERTY', `${path}.mapping.discriminator`, `Discriminator property "${e.mapping.discriminator}" does not exist.`);
    }
    const checkNames = new Set<string>();
    (e.checks ?? []).forEach((c, i) => {
      if (checkNames.has(c.name)) error('E_DUPLICATE_CHECK', `${path}.checks[${i}]`, `Duplicate check constraint "${c.name}".`);
      checkNames.add(c.name);
    });
    checkProperties(path, e.properties);
    checkIndexes(path, e.indexes, visible);
    checkOwned(path, e);
  }

  // Relationships
  const seenFks = new Set<string>();
  model.relationships.forEach((r, i) => {
    const path = `model.relationships[${i}]`;
    const dependent = resolveNode(model, r.dependent);
    const principal = resolveNode(model, r.principal);
    if (!dependent) error('E_UNKNOWN_ENTITY', `${path}.dependent`, `Unknown entity "${r.dependent}".`, 'Use an entity key from model.entities, or Owner.Navigation for owned types.');
    if (!principal) error('E_UNKNOWN_ENTITY', `${path}.principal`, `Unknown entity "${r.principal}".`, 'Use an entity key from model.entities, or Owner.Navigation for owned types.');
    const fkKey = `${r.dependent}|${r.foreignKey.join(',')}|${r.principal}`;
    if (seenFks.has(fkKey)) error('E_DUPLICATE_RELATIONSHIP', path, 'The same relationship is declared twice.');
    seenFks.add(fkKey);
    if (!dependent || !principal) return;
    if (principal.kind === 'owned') {
      error(
        'E_OWNED_PRINCIPAL',
        `${path}.principal`,
        `"${r.principal}" is an owned type; EF Core does not let other entities reference an owned type.`,
        'Make it an entity with its own key (and a relationship to its owner), or reference the owner instead.',
      );
      return;
    }
    const depProps = allProperties(model, dependent);
    const prinProps = allProperties(model, principal);
    for (const p of r.foreignKey) {
      if (!depProps[p]) error('E_FK_PROPERTY', `${path}.foreignKey`, `Foreign key property "${p}" does not exist on "${r.dependent}".`, `Add "${p}" to ${dependent.path}.properties.`);
    }
    const principalKey = r.principalKey ?? keyOf(model, principal);
    if (!principalKey) {
      error('E_PRINCIPAL_KEY', `${path}.principal`, `"${r.principal}" has no key to reference.`);
      return;
    }
    for (const p of principalKey) {
      if (!prinProps[p]) error('E_PRINCIPAL_KEY', `${path}.principalKey`, `Principal key property "${p}" does not exist on "${r.principal}".`);
    }
    if (principalKey.length !== r.foreignKey.length) {
      error('E_FK_ARITY', `${path}.foreignKey`, `Foreign key has ${r.foreignKey.length} properties but the principal key has ${principalKey.length}.`);
      return;
    }
    if (r.principalKey) {
      const keys = [keyOf(model, principal) ?? [], ...alternateKeysOf(model, principal)];
      if (!keys.some((k) => k.length === r.principalKey!.length && k.every((x, j) => x === r.principalKey![j]))) {
        warn('W_PRINCIPAL_KEY_NOT_KEY', `${path}.principalKey`, 'The principal key is not declared as a key; EF Core will create an alternate key.', `Add it to ${principal.path}.alternateKeys.`);
      }
    }
    r.foreignKey.forEach((p, j) => {
      const fk = depProps[p];
      const pk = prinProps[principalKey[j]!];
      if (fk && pk && fk.type !== pk.type) {
        warn('W_FK_TYPE', `${path}.foreignKey`, `"${p}" is ${fk.type} but "${principalKey[j]}" is ${pk.type}.`);
      }
      if (fk && r.required && !fk.required) {
        warn('W_REQUIRED_NULLABLE_FK', `${dependent.path}.properties.${p}`, `Relationship is required but "${p}" allows null.`, 'Set "required": true on the property or make the relationship optional.');
      }
    });
    if (r.required && (r.onDelete === 'setNull' || r.onDelete === 'clientSetNull')) {
      warn('W_SETNULL_REQUIRED', `${path}.onDelete`, `onDelete "${r.onDelete}" cannot null out a required foreign key.`, 'Use "cascade" or "restrict", or make the relationship optional.');
    }
    if (r.dependentNavigation && depProps[r.dependentNavigation]) {
      error('E_NAME_CLASH', `${path}.dependentNavigation`, `"${r.dependentNavigation}" is also a property of "${r.dependent}".`);
    }
    if (r.principalNavigation && prinProps[r.principalNavigation]) {
      error('E_NAME_CLASH', `${path}.principalNavigation`, `"${r.principalNavigation}" is also a property of "${r.principal}".`);
    }
    if (r.requiredDependent && r.cardinality !== 'one-to-one') {
      warn('W_REQUIRED_DEPENDENT', `${path}.requiredDependent`, 'requiredDependent only applies to one-to-one relationships.');
    }
  });

  // Sequences
  const sequenceNames = new Set<string>();
  (model.sequences ?? []).forEach((s, i) => {
    const key = `${s.schema ?? ''}.${s.name}`;
    if (sequenceNames.has(key)) error('E_DUPLICATE_SEQUENCE', `model.sequences[${i}]`, `Duplicate sequence "${s.name}".`);
    sequenceNames.add(key);
  });

  // Renames
  (doc.renames ?? []).forEach((r, i) => {
    const path = `renames[${i}]`;
    if (r.entity) {
      const node = resolveNode(model, r.entity);
      if (!node) error('E_RENAME_TARGET', `${path}.entity`, `Unknown entity "${r.entity}".`);
      else if (!allProperties(model, node)[r.to]) error('E_RENAME_TARGET', `${path}.to`, `"${r.entity}" has no property "${r.to}".`, 'Renames use the new name in "to", as it appears in this model.');
    } else if (!model.entities[r.to]) {
      error('E_RENAME_TARGET', `${path}.to`, `No entity "${r.to}" in this model.`, 'Renames use the new name in "to", as it appears in this model.');
    }
  });

  return issues;
}
